import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { ShieldCheck, Filter, RefreshCw } from 'lucide-react';

export default function AdminAudit() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [archive, setArchive] = useState(null);
  const [filter, setFilter] = useState({ action: '', actor_id: '', days: 7 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.action) params.action = filter.action;
      if (filter.actor_id) params.actor_id = filter.actor_id;
      if (filter.days) params.days = filter.days;
      params.limit = 200;
      const [a, b, c] = await Promise.all([
        api.get('/audit-logs', { params }),
        api.get('/audit-logs/stats', { params: { days: filter.days } }),
        api.get('/audit-logs/archive-stats').catch(() => ({ data: null })),
      ]);
      setLogs(a.data); setStats(b.data); setArchive(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2 inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3"/> COMPLIANCE
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Audit log</h1>
          <p className="text-fg-muted text-sm mt-1">HIPAA-grade record of every privileged action.</p>
        </div>
        <button onClick={load} className="btn-secondary inline-flex items-center gap-2 print:hidden" data-testid="audit-refresh">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-12 gap-4">
        <div className="bento-card md:col-span-4">
          <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-2">Top actions · last {filter.days}d</div>
          {stats.length ? (
            <ul className="text-sm divide-y divide-soft" data-testid="audit-stats">
              {stats.slice(0, 12).map(r => (
                <li key={r.action} className="py-1.5 flex items-center justify-between">
                  <span className="font-mono text-xs">{r.action}</span>
                  <span className="font-bold">{r.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-fg-muted">No actions logged yet.</div>
          )}
          {archive && (
            <div className="mt-4 pt-3 border-t border-soft text-xs" data-testid="audit-archive-stats">
              <div className="text-[10px] uppercase tracking-wider text-fg-muted mb-1">Archive (cold storage)</div>
              <div className="flex items-center justify-between">
                <span className="text-fg-muted">Hot rows</span>
                <span className="font-bold">{archive.hot_count}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-fg-muted">Archived rows</span>
                <span className="font-bold">{archive.archived_count}</span>
              </div>
              <div className="text-[10px] text-fg-muted mt-1">Auto-archives after {archive.retention_days}d.</div>
            </div>
          )}
        </div>

        <div className="bento-card md:col-span-8">
          <div className="flex flex-wrap gap-2 mb-3 items-end print:hidden">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-fg-muted">Filter action</label>
              <input value={filter.action} onChange={e=>setFilter({...filter, action: e.target.value})}
                     className="input text-sm" placeholder="e.g. login.success" data-testid="audit-filter-action"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-fg-muted">Actor ID</label>
              <input value={filter.actor_id} onChange={e=>setFilter({...filter, actor_id: e.target.value})}
                     className="input text-sm" placeholder="user_…" data-testid="audit-filter-actor"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-fg-muted">Days</label>
              <select value={filter.days} onChange={e=>setFilter({...filter, days: Number(e.target.value)})}
                      className="input text-sm" data-testid="audit-filter-days">
                <option value={1}>Last 24h</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
              </select>
            </div>
            <button onClick={load} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="audit-apply">
              <Filter className="w-3.5 h-3.5"/> Apply
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-fg-muted">
                  <th className="text-left py-1.5">When</th>
                  <th className="text-left py-1.5">Action</th>
                  <th className="text-left py-1.5">Actor</th>
                  <th className="text-left py-1.5">Target</th>
                  <th className="text-left py-1.5">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft">
                {logs.map(r => (
                  <tr key={r.audit_id} data-testid={`audit-row-${r.audit_id}`}>
                    <td className="py-2 text-xs whitespace-nowrap text-fg-muted">{new Date(r.at).toLocaleString()}</td>
                    <td className="py-2 font-mono text-xs">{r.action}</td>
                    <td className="py-2 text-xs">{r.actor_role || '—'}<br/><span className="text-fg-muted font-mono text-[10px]">{r.actor_id || ''}</span></td>
                    <td className="py-2 text-xs"><span className="font-mono text-[10px]">{r.target_id || ''}</span></td>
                    <td className="py-2 text-xs text-fg-muted">{r.ip || ''}</td>
                  </tr>
                ))}
                {!logs.length && <tr><td colSpan={5} className="py-6 text-center text-fg-muted">No matches.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
